/**
 * useSprintDetailViews Hook
 *
 * Composes view-related hooks for SprintDetail.
 * Groups: handlers, view props, effective handlers, augmented props.
 */
import { useCallback, useRef, useState } from 'react';
import useDeliverableHandlers from './useDeliverableHandlers';
import useCustomFieldConflict from './useCustomFieldConflict';
import useComposedViewHandlers from './useComposedViewHandlers';
import useResetCampaignProgress from './useResetCampaignProgress';
import useAssembledViewProps from './useAssembledViewProps';

const useSprintDetailViews = ({
  // From core
  localSprint,
  setLocalSprint,
  actions,
  recordAction,
  wrapLastUndoAction,
  startBatch,
  endBatch,
  isMultiCampaignMode,
  undo, redo, canUndo, canRedo, undoStack, redoStack,
  // From features
  setup,
  wrappedToolbar,
  modals,
  nested,
  fokusModeRefs,
  campaignViews,
  effectiveFlowViewVisibility,
  handleFlowViewVisibilityChangeInternal,
  customViewSettings,
  // From state
  state,
  // Props
  allSprints,
  isTemplate,
  settings,
  onSelectSprint,
  onAddSprint,
  onUpdateSprint,
  onRenameSprint,
  onSprintIconChange,
  onTrashSprint,
  onNestCampaign,
  loadFullSprint,
  activeView,
  multiCampaignData,
  onRenameView,
  onViewIconChange,
  onRemoveCampaignFromView,
  onAddCampaignToView,
  removingCampaignId,
  hideViewExplainerModal,
  onHideViewExplainerModalChange,
  flushPendingSprintSave,
}) => {
  const {
    flowchartRef,
    showNestedCampaigns, setShowNestedCampaigns,
    todoViewMode, setTodoViewMode,
    todoSwimlaneGroupBy, setTodoSwimlaneGroupBy,
    tableColumnVisibility, setTableColumnVisibility,
    todoElementVisibility, setTodoElementVisibility,
    setLocalFlowViewVisibility,
    setIsCampaignPickerOpen,
    campaignPickerMode, setCampaignPickerMode, pendingPasteDataRef,
    setDeliverableSubmitModal,
    setDeliverablesModalTaskId,
  } = state;

  const { tasks, statuses, allTags } = setup;

  // Ref to store visible/filtered tasks for Flow view
  // This allows useSprintHandlers to position new nodes relative to visible tasks only
  const visibleTasksRef = useRef(tasks);
  const getVisibleTasks = useCallback(() => visibleTasksRef.current, []);

  const deliverables = useDeliverableHandlers({
    tasks,
    actions,
    setDeliverableSubmitModal,
    setDeliverablesModalTaskId,
  });

  const {
    renameFieldModal,
    setRenameFieldModal,
    onCustomFieldConflict,
  } = useCustomFieldConflict();

  const composed = useComposedViewHandlers({
    tasks,
    statuses,
    allTags,
    allSprints,
    localSprint,
    setLocalSprint,
    nested,
    flowchartRef,
    getVisibleTasks,
    onCustomFieldConflict,
    onAddSprint,
    onUpdateSprint,
    onRenameSprint,
    onTrashSprint,
    onSelectSprint,
    loadFullSprint,
    recordAction,
    startBatch,
    endBatch,
    isMultiCampaignMode,
    isTemplate,
    actions,
    modals,
    setup,
    wrappedToolbar,
    campaignViews,
    customViewSettings,
    handleFlowViewVisibilityChangeInternal,
    setShowNestedCampaigns,
    setTableColumnVisibility,
    setTodoElementVisibility,
    setLocalFlowViewVisibility,
    setTodoViewMode,
    setTodoSwimlaneGroupBy,
    setIsCampaignPickerOpen,
    campaignPickerMode,
    setCampaignPickerMode,
    pendingPasteDataRef,
    onRenameView,
    onViewIconChange,
    todoShowNestedItems: state.todoShowNestedItems,
  });

  const [isTidyToolbarBusy, setIsTidyToolbarBusy] = useState(false);

  const handleManageCustomFields = useCallback(() => {
    state.setIsCustomFieldsManagerOpen(true);
  }, [state]);

  const onResetCampaignProgress = useResetCampaignProgress({
    localSprint,
    setLocalSprint,
    statuses,
    allSprints,
    isMultiCampaignMode,
    loadFullSprint,
    onUpdateSprint,
    modals,
    recordAction,
  });

  const {
    flowFilteredTasks,
    neuralFilteredTasks,
    handleAddKanbanColumn,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    flowProps,
    augmentedKanbanProps,
    augmentedTableProps,
    todoListProps,
    viewSwitcherProps,
  } = useAssembledViewProps({
    core: {
      localSprint,
      setLocalSprint,
      actions,
      recordAction,
      wrapLastUndoAction,
      isMultiCampaignMode,
      allSprints,
      isTemplate,
      settings,
      onSelectSprint,
      onUpdateSprint,
      onSprintIconChange,
      onNestCampaign,
      loadFullSprint,
      activeView,
      multiCampaignData,
      onRemoveCampaignFromView,
      onAddCampaignToView,
      removingCampaignId,
      hideViewExplainerModal,
      onHideViewExplainerModalChange,
      effectiveFlowViewVisibility,
      modals,
      wrappedToolbar,
    },
    setup,
    state,
    nested,
    composed,
    campaignViews,
    fokusModeRefs,
    deliverables,
    viewOptions: {
      isTidyToolbarBusy,
      setIsTidyToolbarBusy,
      handleManageCustomFields,
      onResetCampaignProgress,
    },
    undoRedo: { undo, redo, canUndo, canRedo, undoStack, redoStack },
  });

  // Update visible tasks ref with filtered tasks for Flow view
  // This ensures new nodes are positioned relative to visible tasks only
  visibleTasksRef.current = flowFilteredTasks;

  return {
    // Handlers
    handlers: composed.handlers,
    handleViewRename: composed.handleViewRename,
    handleViewIconChangeLocal: composed.handleViewIconChangeLocal,
    handleCampaignPickerSelect: composed.handleCampaignPickerSelect,
    handleNestTasks: composed.handleNestTasks,
    handleTrashNestedSprint: composed.handleTrashNestedSprint,
    crossSprintActions: composed.crossSprintActions,
    effectiveHandlers: composed.effectiveHandlers,
    handleAddKanbanColumn,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,

    // Progress
    calculatedProgress: composed.calculatedProgress,

    // View props
    flowProps,
    augmentedKanbanProps,
    augmentedTableProps,
    todoListProps,
    viewSwitcherProps,
    neuralFilteredTasks,

    // Modals
    inProgressLockWarningModal: composed.inProgressLockWarningModal,
    renameFieldModal,
    setRenameFieldModal,
  };
};

export default useSprintDetailViews;
